/**
 * Shared helpers for plugins that own [data-panel]/[data-secondary-panel] content for exactly
 * one `activeView` value, keyed on a compound `{ activeView, <id> }` pair via `pairwise()`.
 * Used by lotMapSelection.plugin.js, floorPlanDetail.plugin.js, housingUnitDetail.plugin.js —
 * each subscribes with the same `map → distinctUntilChanged → pairwise() → tap → filter` pipeline
 * shape (see any of their own module docstrings); only the view value, the id field, and what
 * happens on enter/refresh differ per plugin.
 *
 * @module nativeInteractiveSiteMap/services/viewTransition
 */

/**
 * @param {string} viewValue - the activeView constant this plugin owns (e.g. VIEW_FLOOR_PLAN_DETAIL)
 * @param {Function} cleanup - called with no arguments when the pair shows the view was just left
 * @returns {Function} pairwise-pair tap callback: `([previous, current]) => void`
 */
export function onViewLeft(viewValue, cleanup) {
  return ([previous, current]) => {
    const wasInView = previous.activeView === viewValue;
    const isInView = current.activeView === viewValue;
    if (wasInView && !isInView) {
      cleanup();
    }
  };
}

/**
 * @param {string} viewValue - the activeView constant this plugin owns
 * @returns {Function} pairwise-pair filter predicate: `([, current]) => boolean`
 */
export function isViewActive(viewValue) {
  return ([, current]) => current.activeView === viewValue;
}
